import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { StreamingPlatform } from 'types';

export type StreamingPlatformState<Platform = StreamingPlatform, State = PlatformState> = {
  key: Platform;
  url: string | null;
  accessToken: string;
  expiresAt?: number | null;
  isLinked?: boolean;
  isActive?: boolean;
  stopped?: boolean;
  state?: State;
  enabled?: boolean;
};

export type PlatformState = FacebookState | YoutubeState | TwitchState;

export type FacebookState = {
  userId: number;
};
export type YoutubeState = {};
export type TwitchState = {};

export type FacebookStreamingPlatformState = StreamingPlatformState<StreamingPlatform.FACEBOOK, FacebookState>;
export type YoutubeStreamingPlatformState = StreamingPlatformState<StreamingPlatform.YOUTUBE, YoutubeState>;
export type TwitchStreamingPlatformState = StreamingPlatformState<StreamingPlatform.TWITCH, TwitchState>;

export type StreamingPlatformsState = {
  [StreamingPlatform.FACEBOOK]: FacebookStreamingPlatformState;
  [StreamingPlatform.YOUTUBE]: YoutubeStreamingPlatformState;
  [StreamingPlatform.TWITCH]: TwitchStreamingPlatformState;
};

const initialState: StreamingPlatformsState = {
  [StreamingPlatform.FACEBOOK]: {
    key: StreamingPlatform.FACEBOOK,
    isLinked: false,
    isActive: false,
    accessToken: '',
    url: null,
    stopped: false,
    expiresAt: null,
    // TODO: disabled until handling refresh tokens with server side is implemented
    enabled: true
  },
  [StreamingPlatform.YOUTUBE]: {
    key: StreamingPlatform.YOUTUBE,
    isLinked: false,
    isActive: false,
    accessToken: '',
    url: null,
    stopped: false,
    expiresAt: null,
    enabled: true
  },
  [StreamingPlatform.TWITCH]: {
    key: StreamingPlatform.TWITCH,
    isLinked: false,
    isActive: false,
    accessToken: '',
    url: null,
    stopped: false,
    expiresAt: null,
    // TODO: disabled until handling refresh tokens with server side is implemented
    enabled: false
  }
};

export const streamingPlatformsSlice = createSlice({
  name: 'streamingPlatforms',
  initialState,
  reducers: {
    updateStreamingPlatform: (
      state,
      action: PayloadAction<{ platformKey: StreamingPlatform; platformState: Partial<StreamingPlatformState> }>
    ) => ({
      ...state,
      [action.payload.platformKey]: {
        ...state[action.payload.platformKey],
        ...action.payload.platformState,
        key: action.payload.platformKey
      }
    }),
    reset: () => initialState,
    refreshToken: (
      state,
      action: PayloadAction<{
        platformKey: StreamingPlatform;
        state: { accessToken: string; expiresAt: number };
      }>
    ) => {
      const {
        platformKey,
        state: { accessToken, expiresAt }
      } = action.payload;

      // eslint-disable-next-line no-param-reassign
      state[platformKey].accessToken = accessToken;
      // eslint-disable-next-line no-param-reassign
      state[platformKey].expiresAt = expiresAt;
    }
  }
});

export const streamingPlatformsReducer = streamingPlatformsSlice.reducer;
export const streamingPlatformsActions = {
  ...streamingPlatformsSlice.actions
};
